import type { CardDto, Destination } from '@konvert/shared';
import { addDays, addMonths, formatMinutesRu, startOfMonth, startOfWeek } from '@konvert/domain';
import { t } from '../lib/i18n.js';

/**
 * Morning triage (SPEC 5.4, 11.2): the cards left over from finished days, one
 * at a time. There is no silent carry-over — every card gets a decision here.
 */
export function TriageScreen({
  cards,
  today,
  threshold,
  onMove,
  onComplete,
  onDrop,
  onPickDate,
  onClose,
}: {
  cards: CardDto[];
  today: string;
  threshold: number;
  onMove: (card: CardDto, destination: Destination) => void;
  onComplete: (card: CardDto) => void;
  onDrop: (card: CardDto) => void;
  onPickDate: (card: CardDto) => void;
  onClose: () => void;
}): JSX.Element {
  const card = cards[0];

  if (!card) {
    return (
      <section className="envelope space-y-4 p-4">
        <h1 className="text-lg text-ink">{t('triage.title')}</h1>
        <p className="py-6 text-center text-sm text-ink-soft">{t('triage.empty')}</p>
        <button
          type="button"
          onClick={onClose}
          className="w-full rounded-[var(--radius-card)] border border-[var(--line)] px-3 py-2.5 text-ink"
        >
          {t('triage.close')}
        </button>
      </section>
    );
  }

  const options: Array<[string, Destination]> = [
    [t('triage.today'), { kind: 'day', date: today }],
    [t('triage.tomorrow'), { kind: 'day', date: addDays(today, 1) }],
    [t('triage.week'), { kind: 'week', date: startOfWeek(today, 1) }],
    [t('triage.next_week'), { kind: 'week', date: addDays(startOfWeek(today, 1), 7) }],
    [t('triage.month'), { kind: 'month', date: startOfMonth(today) }],
    [t('triage.next_month'), { kind: 'month', date: startOfMonth(addMonths(today, 1)) }],
    [t('triage.someday'), { kind: 'someday' }],
  ];

  return (
    <section className="envelope space-y-4 p-4">
      <header className="flex items-baseline justify-between">
        <h1 className="text-lg text-ink">{t('triage.title')}</h1>
        <span className="text-xs text-ink-soft">
          {t('triage.progress')}: {cards.length}
        </span>
      </header>

      <div className="paper-card space-y-2 p-4">
        <p className="text-[17px] leading-snug text-ink">
          {card.is_private ? '[скрытая карточка]' : card.title}
        </p>
        <p className="flex flex-wrap gap-2 text-xs text-ink-soft">
          {card.est_minutes ? <span>{formatMinutesRu(card.est_minutes)}</span> : null}
          {card.energy ? <span>{t(`energy.${card.energy}` as 'energy.green')}</span> : null}
          {card.reschedule_count > 0 ? (
            <span>
              {t('card.rescheduled')} {card.reschedule_count}
            </span>
          ) : null}
        </p>
        {card.status === 'waiting' ? (
          <p className="text-sm text-ink">
            {t('triage.waiting_question')} {card.waiting_for ? `(${card.waiting_for})` : ''}
          </p>
        ) : null}
        {card.reschedule_count >= threshold ? (
          <p className="rounded-[var(--radius-card)] bg-[var(--kraft)]/40 px-3 py-2 text-xs text-ink">
            {t('triage.advice')}
          </p>
        ) : null}
      </div>

      <div className="grid grid-cols-2 gap-2">
        {options.map(([label, destination]) => (
          <button
            key={label}
            type="button"
            onClick={() => onMove(card, destination)}
            className="paper-card px-3 py-3 text-left text-ink"
          >
            {label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => onPickDate(card)}
          className="paper-card px-3 py-3 text-left text-ink"
        >
          {t('triage.pick_date')}
        </button>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={() => onComplete(card)}
          className="rounded-[var(--radius-card)] bg-[var(--ink)] px-3 py-3 text-[var(--paper)]"
        >
          {t('triage.done')}
        </button>
        <button
          type="button"
          onClick={() => onDrop(card)}
          className="rounded-[var(--radius-card)] border border-[var(--line)] px-3 py-3 text-ink"
        >
          {t('triage.trash')}
        </button>
      </div>

      <button
        type="button"
        onClick={onClose}
        className="w-full py-2 text-sm text-ink-soft"
      >
        {t('triage.close')}
      </button>
    </section>
  );
}
